/**
 * When does the structure breakdown exist on Files?
 *
 * Structures are born on the proposal when the first timber list is applied to
 * it, so Files only offers structure tagging / splitting once the project has a
 * timber list *and* the proposal lists at least one structure. Before that,
 * uploads are plain named tracks (the designer separates work by track title).
 *
 * Designers cannot read `proposalBuilders`, so the proposal structure list may be
 * empty even after a split. Tagged tracks still unlock the breakdown — option
 * labels then come from denormalized stream tags (kept in sync with proposal names).
 */

import type { ProjectFile, ProjectFileStream } from '../types';
import type { StructureRef } from './proposalStructureRefs';
import { anyStreamTagged } from './projectFileStreamGroups';

export const STRUCTURES_LOCKED_HINT =
  'Structures unlock after the first timber list is uploaded and applied to the proposal — or run "Set up structures…" on Files to create them from the timber lists and split the other tracks in one pass.';

/** A timber-list version track with at least one version, or a file tagged as a timber list. */
export function projectHasTimberList(
  streams: ReadonlyArray<Pick<ProjectFileStream, 'streamKind' | 'lastVersionIndex' | 'headFileId'>>,
  files: ReadonlyArray<Pick<ProjectFile, 'kindTag' | 'streamId'>> = [],
): boolean {
  for (const s of streams) {
    if (s.streamKind !== 'timber_list_csv') continue;
    if ((s.lastVersionIndex ?? 0) > 0 || !!s.headFileId) return true;
  }
  for (const f of files) if (f.kindTag === 'timber_list') return true;
  return false;
}

export interface StructureGateInput {
  streams: ReadonlyArray<Pick<ProjectFileStream, 'streamKind' | 'lastVersionIndex' | 'headFileId' | 'structureIds'>>;
  files?: ReadonlyArray<Pick<ProjectFile, 'kindTag' | 'streamId'>>;
  structures: ReadonlyArray<StructureRef>;
}

/** True once the structure breakdown should show (dropdowns, rail, tag button, split / move). */
export function structuresUnlocked(input: StructureGateInput): boolean {
  const hasStructures = input.structures.length > 0 || anyStreamTagged(input.streams);
  if (!hasStructures) return false;
  return projectHasTimberList(input.streams, input.files ?? []);
}

/**
 * True when splitting whole-project tracks per structure is meaningful: structures are
 * unlocked AND the proposal lists at least two. A single-structure job (one barn) has
 * nothing to split — the whole project already is that structure.
 */
export function projectCanSplitByStructure(input: StructureGateInput): boolean {
  return input.structures.length >= 2 && structuresUnlocked(input);
}

/** Tracks that are not yet tagged to any structure — candidates for the split-at-unlock flow. */
export function untaggedStreams<T extends Pick<ProjectFileStream, 'structureIds'>>(streams: ReadonlyArray<T>): T[] {
  return streams.filter(s => (s.structureIds ?? []).filter(Boolean).length === 0);
}
